import React, { Component } from "react";
import { withRouter } from "react-router-dom";
import PropTypes from "prop-types";
import withStyles from "@material-ui/core/styles/withStyles";
import CircularProgress from "@material-ui/core/CircularProgress";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogFooter from "components/Dialog/DialogFooter.jsx";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import InputLabel from "@material-ui/core/InputLabel";
import FormControl from "@material-ui/core/FormControl";

import GridContainer from "components/Grid/GridContainer.jsx";
import GridItem from "components/Grid/GridItem.jsx";
import Card from "components/Card/Card.jsx";
import CardHeader from "components/Card/CardHeader.jsx";
import CardBody from "components/Card/CardBody.jsx";
import Button from "components/CustomButtons/Button.jsx";
import DataTable from "components/DataTable/DataTable.jsx";
import DialogHeader from "components/Dialog/DialogHeader.jsx";

import api from "api/index";
import ViewToolbar from "components/Toolbar/ViewToolbar.jsx";

const styles = {
  header: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  formControl: { width: "100%", marginTop: "16px" },
  errorMsg: { color: "#f44336", fontSize: "13px" }
};

class LecturerCourses extends Component {
  state = {
    lecturer: null,
    assignments: [],
    courses: [],
    semesters: [],
    loading: true,
    open: false,
    form: { courseId: "", semesterId: "" },
    saving: false,
    error: null
  };

  componentDidMount() {
    const { id } = this.props.match.params;
    Promise.all([
      this.fetchLecturer(id),
      this.fetchAssignments(id),
      this.fetchCourses(),
      this.fetchSemesters()
    ]);
  }

  fetchLecturer = async (id) => {
    try {
      const res = await api.get(`/lecturers/${id}`);
      this.setState({ lecturer: res.data.data || res.data });
    } catch (e) { /* silent */ }
  };

  fetchAssignments = async (id) => {
    this.setState({ loading: true });
    try {
      const res = await api.get(`/lecturers/${id}/courses`);
      const d = res.data.data || res.data;
      this.setState({ assignments: Array.isArray(d) ? d : d.data || [], loading: false });
    } catch (e) { this.setState({ loading: false }); }
  };

  fetchCourses = async () => {
    try {
      const res = await api.get("/courses?limit=200");
      const d = res.data.data || res.data;
      this.setState({ courses: Array.isArray(d) ? d : d.data || [] });
    } catch (e) { /* silent */ }
  };

  fetchSemesters = async () => {
    try {
      const res = await api.get("/semesters?limit=20");
      const d = res.data.data || res.data;
      this.setState({ semesters: Array.isArray(d) ? d : d.data || [] });
    } catch (e) { /* silent */ }
  };

  handleAssign = async () => {
    const { form } = this.state;
    const { id } = this.props.match.params;
    if (!form.courseId || !form.semesterId) {
      this.setState({ error: "Course and semester are required." }); return;
    }
    this.setState({ saving: true, error: null });
    try {
      await api.post(`/lecturers/${id}/courses`, form);
      this.setState({ open: false, saving: false, form: { courseId: "", semesterId: "" } });
      this.fetchAssignments(id);
    } catch (e) {
      this.setState({ error: e.response?.data?.message || "Failed to assign.", saving: false });
    }
  };

  handleRemove = async (assignmentId) => {
    const { id } = this.props.match.params;
    if (!window.confirm("Remove this course assignment?")) return;
    try {
      await api.delete(`/lecturers/${id}/courses/${assignmentId}`);
      this.fetchAssignments(id);
    } catch (e) { /* silent */ }
  };

  render() {
    const { classes, history } = this.props;
    const { lecturer, assignments, courses, semesters, loading, open, form, saving, error } = this.state;

    const name = lecturer ? `${lecturer.firstName || ""} ${lecturer.lastName || ""}`.trim() : "Lecturer";

    const columns = [
      { id: "code", header: "Code", accessorFn: a => a.course?.code || "—" },
      { id: "courseName", header: "Course Name", accessorFn: a => a.course?.name || "—" },
      { id: "creditUnits", header: "Credit Units", accessorFn: a => a.course?.creditUnits ? `${a.course.creditUnits} CU` : "—" },
      { id: "semester", header: "Semester", accessorFn: a => a.semester?.name || "—" },
      {
        id: "actions",
        header: "",
        cell: info => (
          <Button size="sm" color="danger" simple onClick={() => this.handleRemove(info.row.original.id)}>Remove</Button>
        )
      }
    ];

    return (
      <GridContainer>
        <GridItem xs={12}>
          <Card>
            <CardHeader color="primary" style={{ margin: -8, padding: "24px 20px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <h4 style={{ margin: 0, color: "#fff", fontWeight: "bold" }}>Course Assignments — {name}</h4>
                  <p style={{ margin: "2px 0 0", color: "rgba(255,255,255,0.8)", fontSize: "13px" }}>
                    {assignments.length} course{assignments.length !== 1 ? "s" : ""} assigned
                  </p>
                </div>
                <div style={{ display: "flex", gap: "8px" }}>
                  <Button size="sm" color="white" simple onClick={() => history.goBack()}>← Back</Button>
                  <Button size="sm" color="white" onClick={() => this.setState({ open: true, error: null })}>
                    + Assign Course
                  </Button>
                </div>
            </CardHeader>
            <CardBody>
              <ViewToolbar tabs={[]} actions={[]} />
              <DataTable
                columns={columns}
                data={assignments}
                isLoading={loading}
                emptyState="No courses assigned to this lecturer."
              />
            </CardBody>
          </Card>
        </GridItem>

        <Dialog open={open} onClose={() => this.setState({ open: false })} maxWidth="sm" fullWidth>
          <DialogHeader title={`Assign Course to ${name}`} />
          <DialogContent>
            {error && <p className={classes.errorMsg}>{error}</p>}
            <FormControl className={classes.formControl}>
              <InputLabel>Course *</InputLabel>
              <Select value={form.courseId}
                onChange={e => { const v = e.target.value; this.setState(prev => ({ form: { ...prev.form, courseId: v } })); }}>
                <MenuItem value="">— Select Course —</MenuItem>
                {courses.map(c => <MenuItem key={c.id} value={c.id}>{c.code} — {c.name}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl className={classes.formControl}>
              <InputLabel>Semester *</InputLabel>
              <Select value={form.semesterId}
                onChange={e => { const v = e.target.value; this.setState(prev => ({ form: { ...prev.form, semesterId: v } })); }}>
                <MenuItem value="">— Select Semester —</MenuItem>
                {semesters.map(s => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
              </Select>
            </FormControl>
          </DialogContent>
          <DialogFooter>
            <Button outlined onClick={() => this.setState({ open: false })} disabled={saving}>Cancel</Button>
            <Button color="primary" onClick={this.handleAssign} disabled={saving}>
              {saving ? <CircularProgress size={18} style={{ color: "#fff" }} /> : "Assign"}
            </Button>
          </DialogFooter>
        </Dialog>
      </GridContainer>
    );
  }
}

LecturerCourses.propTypes = { classes: PropTypes.object.isRequired, history: PropTypes.object.isRequired };
export default withRouter(withStyles(styles)(LecturerCourses));
